/**
 * useDealEditingMachine - Centralized Deal Editing State Machine
 *
 * Phase 1 of the Deals Flow Refactoring
 *
 * This hook consolidates all deal editing state into a single, predictable state machine.
 * It replaces the fragmented useState calls across ViewAgreement, useDealData,
 * useProductRowsDiffControlled, and useDealFieldDiff.
 *
 * Features:
 * - Clear phase transitions: idle → editing → saving → idle/error
 * - Unified dirty tracking for both deal fields and products
 * - Baseline management for cancel/restore operations
 * - Integration with useSaveDealOrchestrator for save operations
 * - Progress tracking for UI feedback
 *
 * @see docs/DEALS_REFACTORING.md for full context
 */

import { useCallback, useMemo, useReducer, useRef } from 'react'
// import isEqual from 'lodash.isequal'
import { diffProducts } from 'utils/dealCalculations'
import { pickForDiff, buildNewValue, deepEqual } from 'components/Deals/utils/diffUtils'
import { calculateDealTotals } from 'utils/dealCalculations'
import type { NewDeal, Product, DealUpdate, ProductChanges } from 'types'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type EditPhase = 'idle' | 'editing' | 'saving' | 'error'

export interface EditSession {
  /** When edit mode was entered */
  startedAt: number
  /** Draft deal (editable copy) */
  draftDeal: NewDeal
  /** Draft products (editable copy) */
  draftProducts: Product[]
}

export interface EditBaselines {
  /** Frozen deal state at edit start */
  deal: NewDeal | null
  /** Frozen deal fields for diffing (normalized) */
  dealPicked: ReturnType<typeof pickForDiff> | null
  /** Frozen products at edit start */
  products: Product[] | null
}

export interface SaveState {
  inFlight: boolean
  error: Error | null
  lastSavedAt: number | null
}

export interface ValidationState {
  isValid: boolean
  errors: Record<string, string>
}

export interface DealEditingState {
  /** Current phase of the editing flow */
  phase: EditPhase

  /** Current deal from source (React Query cache) */
  deal: NewDeal | null

  /** Current products from source (React Query cache) */
  products: Product[]

  /** Active edit session (only when phase === 'editing') */
  editSession: EditSession | null

  /** Baselines for diff comparison and cancel/restore */
  baselines: EditBaselines

  /** Save operation state */
  saveState: SaveState

  /** Validation state */
  validation: ValidationState

  /** Version counter for forcing re-renders */
  version: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Actions
// ─────────────────────────────────────────────────────────────────────────────

export type DealEditingAction =
  | { type: 'LOAD_DATA'; payload: { deal: NewDeal | null; products: Product[] } }
  | { type: 'START_EDIT' }
  | { type: 'UPDATE_DRAFT_DEAL'; payload: { field: string; value: any } }
  | { type: 'UPDATE_DRAFT_PRODUCT'; payload: { index: number; updates: Partial<Product> } }
  | { type: 'ADD_PRODUCT'; payload: { product: Product } }
  | { type: 'REMOVE_PRODUCT'; payload: { index: number } }
  | { type: 'REORDER_PRODUCTS'; payload: { fromIndex: number; toIndex: number } }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SUCCESS'; payload: { deal: NewDeal; products: Product[] } }
  | { type: 'SAVE_ERROR'; payload: { error: Error } }
  | { type: 'CANCEL_EDIT' }
  | { type: 'RESET_ERROR' }
  | { type: 'FORCE_REFRESH' }
  | { type: 'PATCH_DRAFT_DEAL'; payload: { patch: Partial<NewDeal> } }
  | { type: 'SORT_PRODUCTS'; payload: { products: Product[] } }
  | { type: 'RESET_BASELINES'; payload?: { deal?: NewDeal; products?: Product[] } }

// ─────────────────────────────────────────────────────────────────────────────
// Initial State
// ─────────────────────────────────────────────────────────────────────────────

const initialState: DealEditingState = {
  phase: 'idle',
  deal: null,
  products: [],
  editSession: null,
  baselines: {
    deal: null,
    dealPicked: null,
    products: null,
  },
  saveState: {
    inFlight: false,
    error: null,
    lastSavedAt: null,
  },
  validation: {
    isValid: true,
    errors: {},
  },
  version: 0,
}

// ─────────────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────────────

function dealEditingReducer(
  state: DealEditingState,
  action: DealEditingAction
): DealEditingState {
  switch (action.type) {
    case 'LOAD_DATA': {
      const { deal, products } = action.payload

      // If we're in edit mode, don't overwrite edit session
      // but update the source data for conflict detection
      if (state.phase === 'editing') {
        return {
          ...state,
          deal,
          products,
          version: state.version + 1,
        }
      }

      // Guard: if a save just completed (within 2s), the machine already has
      // the correct data from SAVE_SUCCESS. The incoming productRows may be
      // stale (React Query cache hasn't propagated yet), so skip overwriting.
      const recentlySaved = state.saveState.lastSavedAt && (Date.now() - state.saveState.lastSavedAt) < 2000
      if (recentlySaved && state.products.length > 0) {
        // Still update the deal (lightweight, usually correct) but keep products from save
        return {
          ...state,
          deal,
          baselines: {
            deal: deal ? { ...deal } : null,
            dealPicked: deal ? pickForDiff(deal) : null,
            products: state.baselines.products ?? state.products.map(p => ({ ...p })),
          },
          version: state.version + 1,
        }
      }

      // Auto-set baselines when data loads (for diffing)
      return {
        ...state,
        deal,
        products,
        baselines: {
          deal: deal ? { ...deal } : null,
          dealPicked: deal ? pickForDiff(deal) : null,
          products: products.map(p => ({ ...p })),
        },
        version: state.version + 1,
      }
    }

    case 'START_EDIT': {
      if (state.phase !== 'idle') {
        console.warn('[useDealEditingMachine] Cannot start edit: not in idle phase')
        return state
      }

      if (!state.deal) {
        console.warn('[useDealEditingMachine] Cannot start edit: no deal loaded')
        return state
      }

      return {
        ...state,
        phase: 'editing',
        editSession: {
          startedAt: Date.now(),
          draftDeal: { ...state.deal },
          draftProducts: state.products.map(p => ({ ...p })),
        },
        baselines: {
          deal: { ...state.deal },
          dealPicked: pickForDiff(state.deal),
          products: state.products.map(p => ({ ...p })),
        },
        saveState: {
          inFlight: false,
          error: null,
          lastSavedAt: null,
        },
        version: state.version + 1,
      }
    }

    case 'UPDATE_DRAFT_DEAL': {
      if (state.phase !== 'editing' || !state.editSession) {
        console.warn('[useDealEditingMachine] Cannot update draft: not in edit mode')
        return state
      }

      const { field, value } = action.payload
      const updatedDraftDeal = {
        ...state.editSession.draftDeal,
        [field]: value,
      }

      return {
        ...state,
        editSession: {
          ...state.editSession,
          draftDeal: updatedDraftDeal,
        },
        version: state.version + 1,
      }
    }

    case 'UPDATE_DRAFT_PRODUCT': {
      if (state.phase !== 'editing' || !state.editSession) {
        console.warn('[useDealEditingMachine] Cannot update product: not in edit mode')
        return state
      }

      const { index, updates } = action.payload
      const updatedProducts = state.editSession.draftProducts.map((p, i) =>
        i === index ? { ...p, ...updates } : p
      )

      return {
        ...state,
        editSession: {
          ...state.editSession,
          draftProducts: updatedProducts,
        },
        version: state.version + 1,
      }
    }

    case 'ADD_PRODUCT': {
      if (state.phase !== 'editing' || !state.editSession) {
        console.warn('[useDealEditingMachine] Cannot add product: not in edit mode')
        return state
      }

      return {
        ...state,
        editSession: {
          ...state.editSession,
          draftProducts: [...state.editSession.draftProducts, action.payload.product],
        },
        version: state.version + 1,
      }
    }

    case 'REMOVE_PRODUCT': {
      if (state.phase !== 'editing' || !state.editSession) {
        console.warn('[useDealEditingMachine] Cannot remove product: not in edit mode')
        return state
      }

      const { index } = action.payload
      const updatedProducts = state.editSession.draftProducts.filter((_, i) => i !== index)

      return {
        ...state,
        editSession: {
          ...state.editSession,
          draftProducts: updatedProducts,
        },
        version: state.version + 1,
      }
    }

    case 'REORDER_PRODUCTS': {
      if (state.phase !== 'editing' || !state.editSession) {
        console.warn('[useDealEditingMachine] Cannot reorder: not in edit mode')
        return state
      }

      const { fromIndex, toIndex } = action.payload
      const products = [...state.editSession.draftProducts]
      const [removed] = products.splice(fromIndex, 1)
      products.splice(toIndex, 0, removed)

      // Update sortOrder
      const reordered = products.map((p, i) => ({ ...p, sortOrder: i }))

      return {
        ...state,
        editSession: {
          ...state.editSession,
          draftProducts: reordered,
        },
        version: state.version + 1,
      }
    }

    case 'SAVE_START': {
      if (state.phase !== 'editing') {
        console.warn('[useDealEditingMachine] Cannot save: not in edit mode')
        return state
      }

      return {
        ...state,
        phase: 'saving',
        saveState: {
          ...state.saveState,
          inFlight: true,
          error: null,
        },
        version: state.version + 1,
      }
    }

    case 'SAVE_SUCCESS': {
      const { deal, products } = action.payload

      return {
        ...state,
        phase: 'idle',
        deal,
        products,
        editSession: null,
        baselines: {
          deal: { ...deal },
          dealPicked: pickForDiff(deal),
          products: products.map(p => ({ ...p })),
        },
        saveState: {
          inFlight: false,
          error: null,
          lastSavedAt: Date.now(),
        },
        version: state.version + 1,
      }
    }

    case 'SAVE_ERROR': {
      return {
        ...state,
        phase: 'error',
        saveState: {
          ...state.saveState,
          inFlight: false,
          error: action.payload.error,
        },
        version: state.version + 1,
      }
    }

    case 'CANCEL_EDIT': {
      if (state.phase !== 'editing' && state.phase !== 'error') {
        console.warn('[useDealEditingMachine] Cannot cancel: not in edit/error mode')
        return state
      }

      // Restore from baselines
      return {
        ...state,
        phase: 'idle',
        deal: state.baselines.deal,
        products: state.baselines.products ?? [],
        editSession: null,
        saveState: {
          inFlight: false,
          error: null,
          lastSavedAt: state.saveState.lastSavedAt,
        },
        version: state.version + 1,
      }
    }

    case 'RESET_ERROR': {
      if (state.phase !== 'error') {
        return state
      }

      // Go back to editing mode so user can retry
      return {
        ...state,
        phase: 'editing',
        saveState: {
          ...state.saveState,
          error: null,
        },
        version: state.version + 1,
      }
    }

    case 'FORCE_REFRESH': {
      return {
        ...state,
        version: state.version + 1,
      }
    }

    case 'PATCH_DRAFT_DEAL': {
      if (state.phase !== 'editing' || !state.editSession) {
        console.warn('[useDealEditingMachine] Cannot patch draft: not in edit mode')
        return state
      }

      return {
        ...state,
        editSession: {
          ...state.editSession,
          draftDeal: {
            ...state.editSession.draftDeal,
            ...action.payload.patch,
          },
        },
        version: state.version + 1,
      }
    }

    case 'SORT_PRODUCTS': {
      if (state.phase !== 'editing' || !state.editSession) {
        console.warn('[useDealEditingMachine] Cannot sort products: not in edit mode')
        return state
      }

      return {
        ...state,
        editSession: {
          ...state.editSession,
          draftProducts: action.payload.products,
        },
        version: state.version + 1,
      }
    }

    case 'RESET_BASELINES': {
      const deal = action.payload?.deal ?? state.deal
      const products = action.payload?.products ?? state.products
      return {
        ...state,
        baselines: {
          deal: deal ? { ...deal } : null,
          dealPicked: deal ? pickForDiff(deal) : null,
          products: products ? products.map(p => ({ ...p })) : [],
        },
        version: state.version + 1,
      }
    }

    default:
      return state
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Fields to track for deal diffing
// ─────────────────────────────────────────────────────────────────────────────

const DIFF_FIELDS = [
  'type',
  'numberOfPayments',
  'startDate',
  'endDate',
  'dependenciesDueDate',
  'targetLaunchDate',
  'recipients',
  'implementationFee',
  'noSignature',
  'dba',
] as const

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export interface UseDealEditingMachineOptions {
  /** Initial deal data */
  deal?: NewDeal | null
  /** Initial products */
  products?: Product[]
}

export function useDealEditingMachine(options: UseDealEditingMachineOptions = {}) {
  const [state, dispatch] = useReducer(dealEditingReducer, {
    ...initialState,
    deal: options.deal ?? null,
    products: options.products ?? [],
    baselines: {
      deal: options.deal ? { ...options.deal } : null,
      dealPicked: options.deal ? pickForDiff(options.deal) : null,
      products: options.products?.map(p => ({ ...p })) ?? [],
    },
  })

  // ─────────────────────────────────────────────────────────────────────────
  // Derived state
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Current deal (draft if editing, source otherwise)
   */
  const currentDeal = useMemo(() => {
    if (state.phase === 'editing' || state.phase === 'saving') {
      return state.editSession?.draftDeal ?? state.deal
    }
    return state.deal
  }, [state.phase, state.editSession?.draftDeal, state.deal])

  /**
   * Current products (draft if editing, source otherwise)
   */
  const currentProducts = useMemo(() => {
    if (state.phase === 'editing' || state.phase === 'saving') {
      return state.editSession?.draftProducts ?? state.products
    }
    return state.products
  }, [state.phase, state.editSession?.draftProducts, state.products])

  /**
   * Deal field changes (for server update)
   */
  const dealUpdates = useMemo((): DealUpdate[] => {
    if (!state.baselines.dealPicked || !currentDeal) return []

    const baseline = state.baselines.dealPicked
    const current = pickForDiff(currentDeal)

    const changedFields = DIFF_FIELDS.filter(
      field => !deepEqual((baseline as any)[field], (current as any)[field])
    )

    if (changedFields.length === 0) return []

    return changedFields.map(fieldName => ({
      tableName: 'ORGANIZATIONS_TABLE',
      PK: currentDeal.PK,
      SK: currentDeal.SK,
      fieldName,
      newValue: buildNewValue(fieldName, (current as any)[fieldName]),
    }))
  }, [state.baselines.dealPicked, currentDeal, state.version])

  /**
   * Product changes (created, updated, deleted)
   */
  const productChanges = useMemo((): ProductChanges => {
    const baseline = state.baselines.products ?? []
    const current = currentProducts ?? []

    return diffProducts(baseline, current)
  }, [state.baselines.products, currentProducts, state.version])

  /**
   * Is deal dirty (any field changed)?
   */
  const isDealDirty = useMemo(() => {
    if (!state.baselines.dealPicked || !currentDeal) return false
    return !deepEqual(state.baselines.dealPicked, pickForDiff(currentDeal))
  }, [state.baselines.dealPicked, currentDeal, state.version])

  /**
   * Are products dirty (any product created/updated/deleted)?
   */
  const isProductsDirty = useMemo(() => {
    return (
      productChanges.created.length > 0 ||
      productChanges.updated.length > 0 ||
      productChanges.deletedIds.length > 0
    )
  }, [productChanges])

  /**
   * Is anything dirty?
   */
  const isDirty = isDealDirty || isProductsDirty

  /**
   * Calculated totals for current state
   */
  const totals = useMemo(() => {
    if (!currentDeal) return null
    return calculateDealTotals(currentDeal, currentProducts)
  }, [currentDeal, currentProducts])

  /**
   * Check if recipients changed (for special handling)
   */
  const recipientsChanged = useMemo(() => {
    return dealUpdates.some(u => u.fieldName === 'recipients')
  }, [dealUpdates])

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Load/sync data from external source (React Query)
   */
  const loadData = useCallback((deal: NewDeal | null, products: Product[]) => {
    dispatch({ type: 'LOAD_DATA', payload: { deal, products } })
  }, [])

  /**
   * Enter edit mode
   */
  const startEdit = useCallback(() => {
    dispatch({ type: 'START_EDIT' })
  }, [])

  /**
   * Update a deal field in draft
   */
  const updateDealField = useCallback((field: string, value: any) => {
    dispatch({ type: 'UPDATE_DRAFT_DEAL', payload: { field, value } })
  }, [])

  /**
   * Update a product in draft
   */
  const updateProduct = useCallback((index: number, updates: Partial<Product>) => {
    dispatch({ type: 'UPDATE_DRAFT_PRODUCT', payload: { index, updates } })
  }, [])

  /**
   * Add a new product to draft
   */
  const addProduct = useCallback((product: Product) => {
    dispatch({ type: 'ADD_PRODUCT', payload: { product } })
  }, [])

  /**
   * Remove a product from draft
   */
  const removeProduct = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_PRODUCT', payload: { index } })
  }, [])

  /**
   * Reorder products in draft
   */
  const reorderProducts = useCallback((fromIndex: number, toIndex: number) => {
    dispatch({ type: 'REORDER_PRODUCTS', payload: { fromIndex, toIndex } })
  }, [])

  /**
   * Start save operation
   */
  const startSave = useCallback(() => {
    dispatch({ type: 'SAVE_START' })
  }, [])

  /**
   * Mark save as successful
   */
  const saveSuccess = useCallback((deal: NewDeal, products: Product[]) => {
    dispatch({ type: 'SAVE_SUCCESS', payload: { deal, products } })
  }, [])

  /**
   * Mark save as failed
   */
  const saveError = useCallback((error: Error) => {
    dispatch({ type: 'SAVE_ERROR', payload: { error } })
  }, [])

  /**
   * Cancel edit mode and restore from baseline
   */
  const cancelEdit = useCallback(() => {
    dispatch({ type: 'CANCEL_EDIT' })
  }, [])

  /**
   * Clear error and return to editing
   */
  const resetError = useCallback(() => {
    dispatch({ type: 'RESET_ERROR' })
  }, [])

  /**
   * Force a re-render
   */
  const forceRefresh = useCallback(() => {
    dispatch({ type: 'FORCE_REFRESH' })
  }, [])

  /**
   * Patch multiple deal fields at once in draft
   */
  const patchDealDraft = useCallback((patch: Partial<NewDeal>) => {
    dispatch({ type: 'PATCH_DRAFT_DEAL', payload: { patch } })
  }, [])

  /**
   * Replace draft products with a sorted/reordered array
   */
  const sortProductsDraft = useCallback((products: Product[]) => {
    dispatch({ type: 'SORT_PRODUCTS', payload: { products } })
  }, [])

  /**
   * Reset baselines without changing phase (for non-edit mutations)
   */
  const resetBaselines = useCallback((deal?: NewDeal, products?: Product[]) => {
    dispatch({ type: 'RESET_BASELINES', payload: { deal, products } })
  }, [])

  // ─────────────────────────────────────────────────────────────────────────
  // Convenience getters
  // ─────────────────────────────────────────────────────────────────────────

  const isEditing = state.phase === 'editing'
  const isSaving = state.phase === 'saving'
  const isError = state.phase === 'error'
  const isIdle = state.phase === 'idle'
  const canEdit = isIdle && state.deal !== null
  const canSave = isEditing && isDirty
  const canCancel = isEditing || isError

  // ─────────────────────────────────────────────────────────────────────────
  // Return API
  // ─────────────────────────────────────────────────────────────────────────

  return {
    // State
    state,
    phase: state.phase,

    // Current data (draft if editing, source otherwise)
    deal: currentDeal,
    products: currentProducts,
    totals,

    // Source data (from React Query)
    sourceDeal: state.deal,
    sourceProducts: state.products,

    // Edit session
    editSession: state.editSession,
    baselines: state.baselines,

    // Dirty tracking
    isDirty,
    isDealDirty,
    isProductsDirty,
    dealUpdates,
    productChanges,
    recipientsChanged,

    // Save state
    saveState: state.saveState,

    // Phase flags
    isEditing,
    isSaving,
    isError,
    isIdle,
    canEdit,
    canSave,
    canCancel,

    // Actions
    loadData,
    startEdit,
    updateDealField,
    updateProduct,
    addProduct,
    removeProduct,
    reorderProducts,
    startSave,
    saveSuccess,
    saveError,
    cancelEdit,
    resetError,
    forceRefresh,
    patchDealDraft,
    sortProductsDraft,
    resetBaselines,

    // Raw dispatch for advanced use
    dispatch,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Integration helper: Connect to React Query
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Helper hook that connects useDealEditingMachine to React Query data
 *
 * Usage:
 * ```typescript
 * const { deal, productRows } = useDealData(PK, SK, dealId)
 * const machine = useDealEditingMachineWithData(deal, productRows)
 * ```
 */
export function useDealEditingMachineWithData(
  deal: NewDeal | null | undefined,
  products: Product[] | undefined
) {
  const machine = useDealEditingMachine()
  const prevDealRef = useRef<string | null>(null)

  // Sync data when it changes (but not during editing)
  const dealSK = deal?.SK ?? null
  if (dealSK !== prevDealRef.current && machine.phase === 'idle') {
    prevDealRef.current = dealSK
    if (deal) {
      machine.loadData(deal, products ?? [])
    }
  }

  return machine
}

export type DealEditingMachine = ReturnType<typeof useDealEditingMachine>
